import type { ReactNode } from 'react';

interface Props {
  /** Превью-контент за «решёткой» (виден сквозь штриховку, кликабелен только до CTA). */
  children?: ReactNode;
  /** Заголовок premium-блока (например, «Полная финансовая отчётность»). */
  title?: string;
  /** Описание зачем платить. */
  description?: string;
  /** Текст единственной CTA-кнопки. По умолчанию — стандарт PCP. */
  ctaText?: string;
  /** Callback на клик по CTA. */
  onCta?: () => void;
  /** href для CTA (если не нужен JS callback). */
  ctaHref?: string;
}

/**
 * Locked premium-блок — диагональная gold-штриховка + CTA «Открыть полный
 * отчёт». Используется на местах где данные есть, но видно только превью
 * до подписки. Согласно `knowledge/STATES.md` — единственный канонический
 * способ сообщить «это платно».
 *
 * Не блокируется JavaScript — даже без JS пользователь видит CTA-ссылку
 * (если ctaHref передан) и понимает что это premium.
 */
export function LockedBlock({
  children,
  title,
  description,
  ctaText = 'Открыть полный отчёт 7 дней бесплатно',
  onCta,
  ctaHref,
}: Props) {
  return (
    <div
      className="locked-block"
      role="region"
      aria-label="Платный раздел"
      style={{
        position: 'relative',
        border: '1px solid var(--gold-500, #c79a2c)',
        borderRadius: 12,
        background: 'var(--surface, #ffffff)',
        overflow: 'hidden',
      }}
    >
      {/* Превью-контент за штриховкой */}
      {children && (
        <div
          style={{
            padding: 20,
            opacity: 0.6,
            pointerEvents: 'none',
            filter: 'blur(0.5px)',
          }}
        >
          {children}
        </div>
      )}
      {/* Gold штриховка-overlay */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'repeating-linear-gradient(135deg, rgba(199, 154, 44, 0.08) 0 8px, transparent 8px 18px)',
          pointerEvents: 'none',
        }}
      />
      {/* CTA в нижней секции */}
      <div
        style={{
          position: 'relative',
          padding: '20px 20px 24px',
          borderTop: children ? '1px solid var(--border, #e6e2d6)' : 'none',
          textAlign: 'center',
        }}
      >
        {title && (
          <h3
            style={{
              fontFamily: 'Fraunces, Georgia, serif',
              fontSize: 20,
              fontWeight: 600,
              color: 'var(--navy-700, #0b1736)',
              margin: '0 0 8px',
            }}
          >
            {title}
          </h3>
        )}
        {description && (
          <p style={{ color: 'var(--muted, #6b7287)', margin: '0 0 16px', fontSize: 14 }}>
            {description}
          </p>
        )}
        {ctaHref ? (
          <a
            href={ctaHref}
            data-cta="locked-block-unlock"
            style={ctaStyle}
            onClick={(e) => {
              if (onCta) {
                e.preventDefault();
                onCta();
              }
            }}
          >
            {ctaText}
          </a>
        ) : (
          <button
            type="button"
            data-cta="locked-block-unlock"
            onClick={onCta}
            style={ctaStyle}
          >
            {ctaText}
          </button>
        )}
      </div>
    </div>
  );
}

const ctaStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '12px 24px',
  background: 'var(--gold-500, #c79a2c)',
  color: '#1a1a1a',
  fontWeight: 600,
  fontSize: 15,
  textDecoration: 'none',
  border: 'none',
  borderRadius: 999,
  cursor: 'pointer',
  letterSpacing: '0.01em',
};
